import { Box, TextField, Button, InputAdornment, CircularProgress } from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import Breadcrumb from "@/components/Breadcrumb";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { GridRowSelectionModel } from "@mui/x-data-grid";
import { DefaultSecondaryButton } from "@/styles/globalstyles";
import { useQueryHandler } from "@/hooks/useQueryHandler";
import { usePermissions } from "@/hooks/usePermissions";
import {
  useGetManualMasterParts,
  useBulkCreateManualMasterPart,
  useDeleteManualMasterPart,
  useBulkDeleteManualMasterPart,
} from "@/hooks/manualMasterPartHooks";
import { getAxiosErrorMessage } from "@/utils/healper";
import { useToast } from "@/hooks/useToast";
import { ConfirmAlert, ErrorAlert } from "@/utils/alert";
import type {
  ManualMasterPartListItem,
  ManualMasterPartPair,
} from "@/api/manualMasterPartService";
import ManualMasterPartTable from "./ManualMasterPartTable";
import AddManualMasterPartModal from "./AddManualMasterPartModal";

const EMPTY_SELECTION: GridRowSelectionModel = { type: "include", ids: new Set() };

interface DebouncedSearchInputProps {
  initialValue: string;
  isLoading: boolean;
  onSearch: (value: string) => void;
}

const DebouncedSearchInput = ({
  initialValue,
  isLoading,
  onSearch,
}: DebouncedSearchInputProps) => {
  const [value, setValue] = useState<string>(initialValue);
  const isPending = value !== initialValue;

  useEffect(() => {
    const timer = setTimeout(() => onSearch(value), 700);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <TextField
      label="ค้นหา Part Number / Supplier Code"
      variant="outlined"
      size="small"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      slotProps={{
        input: {
          endAdornment: (isLoading || isPending) && (
            <InputAdornment position="end">
              <CircularProgress size={16} />
            </InputAdornment>
          ),
        },
      }}
      sx={{
        mt: 2,
        mb: 1,
        width: "40%",
        "& .MuiInputLabel-root": {
          fontSize: "0.8rem",
          color: "#666666",
          "&.Mui-focused": { color: "primary.main" },
        },
        "& .MuiOutlinedInput-root": {
          "& fieldset": { borderColor: "#cccccc" },
          "&:hover fieldset": { borderColor: "#999999" },
        },
      }}
    />
  );
};

const ManualMasterPartManagement = () => {
  const canEdit = usePermissions().canEdit("master.manualpart");
  const { showToast } = useToast();
  const [openModal, setOpenModal] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>(EMPTY_SELECTION);

  const [searchParams, setSearchParams] = useSearchParams();
  const searchFilter = useMemo(() => searchParams.get("search") || "", [searchParams]);

  const handleSearch = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          if ((prev.get("search") || "") === value) return prev;
          const newParams = new URLSearchParams(prev);
          if (value) {
            newParams.set("search", value);
          } else {
            newParams.delete("search");
          }
          return newParams;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const {
    data: listResponse,
    isLoading,
    isError,
  } = useGetManualMasterParts({
    skip: 0,
    limit: 1000,
    search: searchFilter || undefined,
  });

  const { mutate: bulkCreate, isPending: createPending } =
    useBulkCreateManualMasterPart();
  const { mutate: deleteRow, isPending: deletePending } =
    useDeleteManualMasterPart();
  const { mutate: bulkDeleteRows, isPending: bulkDeletePending } =
    useBulkDeleteManualMasterPart();

  useEffect(() => {
    document.title = "Master Part (Manual) | AMPA";
  }, []);

  // isFetching ไม่รวม isLoading/deletePending/bulkDeletePending ตั้งใจ — ให้ตาราง
  // (DataGrid loading prop) โชว์ skeleton ของตัวเองแทน ไม่เอา full-page overlay
  // ทับทุกครั้งที่ list โหลดใหม่ (เช่นตอนพิมพ์ค้นหา)
  useQueryHandler({ isError });

  const data = useMemo(() => listResponse?.data ?? [], [listResponse]);

  const isTableLoading = isLoading || deletePending || bulkDeletePending;

  const selectedIds = useMemo(() => {
    if (rowSelectionModel.type === "include") {
      return Array.from(rowSelectionModel.ids) as number[];
    }
    return data
      .map((row) => row.id)
      .filter((id) => !rowSelectionModel.ids.has(id));
  }, [rowSelectionModel, data]);

  const handleAdd = () => {
    setOpenModal(true);
  };

  const handleSubmitPairs = (pairs: ManualMasterPartPair[]) => {
    bulkCreate(pairs, {
      onSuccess: () => {
        showToast(`เพิ่ม Master Part (Manual) สำเร็จ ${pairs.length} รายการ`, "success");
        setOpenModal(false);
      },
      onError: (error) => {
        const message = getAxiosErrorMessage(error);
        ErrorAlert("เพิ่มไม่สำเร็จ", `เกิดข้อผิดพลาดในการเพิ่ม: ${message}`);
      },
    });
  };

  const handleDelete = async (row: ManualMasterPartListItem) => {
    const confirmed = await ConfirmAlert(
      "ยืนยันการลบ",
      `ต้องการลบ ${row.part_number} (supplier ${row.supplier_code}) ออกจากรายการ Master Part (Manual) หรือไม่?`,
      "ยืนยัน",
      "ยกเลิก",
    );
    if (confirmed) {
      deleteRow(row.id, {
        onSuccess: () => {
          showToast(`${row.part_number} ถูกลบแล้ว`, "success");
        },
        onError: (error) => {
          const message = getAxiosErrorMessage(error);
          ErrorAlert("ลบไม่สำเร็จ", `เกิดข้อผิดพลาดในการลบ: ${message}`);
        },
      });
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    const confirmed = await ConfirmAlert(
      "ยืนยันการลบ",
      `ต้องการลบ ${selectedIds.length} รายการที่เลือกออกจาก Master Part (Manual) หรือไม่?`,
      "ยืนยัน",
      "ยกเลิก",
    );
    if (confirmed) {
      bulkDeleteRows(selectedIds, {
        onSuccess: () => {
          showToast(`ลบสำเร็จ ${selectedIds.length} รายการ`, "success");
          setRowSelectionModel(EMPTY_SELECTION);
        },
        onError: (error) => {
          const message = getAxiosErrorMessage(error);
          ErrorAlert("ลบไม่สำเร็จ", `เกิดข้อผิดพลาดในการลบ: ${message}`);
        },
      });
    }
  };

  return (
    <Box sx={{ px: 3, py: 3 }}>
      <Breadcrumb current="master.manualpart" />
      <Box sx={{ width: "100%" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <DebouncedSearchInput
            initialValue={searchFilter}
            isLoading={isLoading}
            onSearch={handleSearch}
          />
          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", mt: 2, mb: 1 }}>
            {selectedIds.length > 0 && (
              <Button
                size="small"
                variant="outlined"
                sx={{
                  ...DefaultSecondaryButton,
                  color: "#d32f2f",
                  borderColor: "#d32f2f",
                }}
                onClick={handleBulkDelete}
                disabled={!canEdit || bulkDeletePending}
              >
                <Delete /> ลบที่เลือก ({selectedIds.length})
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              onClick={handleAdd}
              sx={{ ...DefaultSecondaryButton, alignItems: "center" }}
              disabled={!canEdit}
            >
              <Add /> เพิ่ม Master Part (Manual)
            </Button>
          </Box>
        </Box>
        <Box sx={{ mt: 1, borderRadius: 1 }}>
          <ManualMasterPartTable
            data={data}
            isLoading={isTableLoading}
            canEdit={canEdit}
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={setRowSelectionModel}
            onDelete={handleDelete}
          />
        </Box>
      </Box>

      <AddManualMasterPartModal
        open={openModal}
        handleClose={() => setOpenModal(false)}
        handleSubmit={handleSubmitPairs}
        isSubmitting={createPending}
      />
    </Box>
  );
};

export default ManualMasterPartManagement;
